import { useState, useCallback } from 'react';
import SAR from '@/components/feature/SAR';
import { Link } from 'react-router-dom';
import DashboardStats from './components/DashboardStats';
import DashboardCampaigns from './components/DashboardCampaigns';
import DashboardDonors from './components/DashboardDonors';
import DashboardChart from './components/DashboardChart';
import WithdrawalModal from './components/WithdrawalModal';
import PostUpdateModal from './components/PostUpdateModal';
import { dashboardProfile, notifications, withdrawalHistory } from '@/mocks/dashboard';
import { campaigns } from '@/mocks/campaigns';

type Tab = 'overview' | 'campaigns' | 'donors' | 'withdrawal' | 'history';

const DashboardPage = () => {
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [showWithdrawal, setShowWithdrawal] = useState(false);
  const [showPostUpdate, setShowPostUpdate] = useState(false);

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const exportPDF = useCallback(() => {
    const fmt = (n: number) => new Intl.NumberFormat('en-US').format(n);
    const completed = withdrawalHistory.filter(w => w.status === 'completed');
    const totalNet = completed.reduce((s, w) => s + w.net, 0);
    const totalFee = completed.reduce((s, w) => s + w.fee, 0);

    const rows = [...withdrawalHistory].reverse().map(w => {
      const statusLabel = w.status === 'completed' ? 'مكتملة' : w.status === 'pending' ? 'قيد الانتظار' : 'جاري التحويل';
      const date = new Date(w.date).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
      return `
        <tr>
          <td>${date}</td>
          <td>${w.reference}</td>
          <td>${w.bankName} •••• ${w.accountLast4}</td>
          <td>${fmt(w.amount)} ر.س</td>
          <td>${w.fee > 0 ? fmt(w.fee) + ' ر.س' : '-'}</td>
          <td>${fmt(w.net)} ر.س</td>
          <td class="status-${w.status}">${statusLabel}</td>
        </tr>`;
    }).join('');

    const html = `
      <!DOCTYPE html>
      <html dir="rtl" lang="ar">
      <head>
        <meta charset="UTF-8"/>
        <title>تاريخ السحوبات - منصة هِبة</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; color: #1c1917; padding: 32px; font-size: 13px; direction: rtl; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 28px; padding-bottom: 16px; border-bottom: 2px solid #059669; }
          .brand { font-size: 22px; font-weight: 900; color: #059669; }
          .meta { text-align: left; color: #78716c; font-size: 11px; }
          .title { font-size: 18px; font-weight: 800; margin-bottom: 20px; }
          .summary { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 24px; }
          .summary-card { background: #f5f5f4; border-radius: 8px; padding: 14px; text-align: center; }
          .summary-card .val { font-size: 16px; font-weight: 900; color: #059669; }
          .summary-card .lbl { font-size: 11px; color: #78716c; margin-top: 4px; }
          table { width: 100%; border-collapse: collapse; }
          th { background: #f5f5f4; padding: 10px 12px; text-align: right; font-size: 11px; font-weight: 700; color: #57534e; border-bottom: 1px solid #e7e5e4; }
          td { padding: 10px 12px; border-bottom: 1px solid #f5f5f4; font-size: 12px; }
          tr:last-child td { border-bottom: none; }
          .status-completed { color: #059669; font-weight: 700; }
          .status-pending { color: #d97706; font-weight: 700; }
          .status-processing { color: #0284c7; font-weight: 700; }
          .footer { margin-top: 28px; padding-top: 14px; border-top: 1px solid #e7e5e4; color: #a8a29e; font-size: 11px; display: flex; justify-content: space-between; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="brand">منصة هِبة</div>
          <div class="meta">
            <div>${dashboardProfile.displayName}</div>
            <div>تاريخ الإصدار: ${new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
          </div>
        </div>
        <div class="title">كشف تاريخ السحوبات</div>
        <div class="summary">
          <div class="summary-card"><div class="val">$<SAR amount={totalNet} iconSize={12} /></div><div class="lbl">إجمالي المسحوب</div></div>
          <div class="summary-card"><div class="val">${completed.length}</div><div class="lbl">عمليات مكتملة</div></div>
          <div class="summary-card"><div class="val">$<SAR amount={totalFee} iconSize={12} /></div><div class="lbl">إجمالي الرسوم</div></div>
        </div>
        <table>
          <thead>
            <tr>
              <th>التاريخ</th>
              <th>رقم المرجع</th>
              <th>الحساب البنكي</th>
              <th>المبلغ الإجمالي</th>
              <th>الرسوم</th>
              <th>الصافي</th>
              <th>الحالة</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
        <div class="footer">
          <span>منصة هِبة — منصة غير ربحية مرخصة في المملكة العربية السعودية</span>
          <span>هذا الكشف للأغراض المعلوماتية فقط</span>
        </div>
      </body>
      </html>`;

    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); }, 400);
  }, []);
  const myCampaigns = campaigns.filter((c) => ['1'].includes(c.id));

  const tabs: { key: Tab; icon: string; label: string }[] = [
    { key: 'overview',   icon: 'ri-dashboard-line',    label: 'نظرة عامة' },
    { key: 'campaigns',  icon: 'ri-flag-line',          label: 'حملاتي' },
    { key: 'donors',     icon: 'ri-user-heart-line',    label: 'الداعمون' },
    { key: 'withdrawal', icon: 'ri-bank-line',          label: 'السحب' },
    { key: 'history',    icon: 'ri-history-line',        label: 'تاريخ السحوبات' },
  ];

  return (
    <div className="min-h-screen bg-stone-50" dir="rtl">

      {/* Top Bar */}
      <div className="bg-white border-b border-stone-100 pt-20">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-stone-400">مرحباً بعودتك</p>
            <h1 className="text-lg font-black text-stone-800">{dashboardProfile.displayName}</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowPostUpdate(true)}
              className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-edit-box-line text-sm"></i>
              نشر تحديث
            </button>
            <Link
              to="/notifications"
              className="relative w-10 h-10 flex items-center justify-center rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-600 transition-colors cursor-pointer"
            >
              <i className="ri-notification-3-line text-lg"></i>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center bg-red-500 text-white text-xs rounded-full font-bold">
                  {unreadCount}
                </span>
              )}
            </Link>
            <Link
              to={`/profile/${dashboardProfile.username}`}
              className="flex items-center gap-2 cursor-pointer"
            >
              <img
                src={dashboardProfile.avatar}
                alt={dashboardProfile.displayName}
                className="w-10 h-10 rounded-xl object-cover object-top border-2 border-stone-100"
              />
            </Link>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto">
          {/* Tabs row */}
          <div className="flex items-center justify-between border-b border-stone-100 px-4 md:px-8">
            <div className="flex min-w-0 flex-1">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center justify-center gap-1 flex-1 min-w-0 py-3 text-[10px] sm:text-xs md:text-sm font-semibold border-b-2 -mb-px transition-all cursor-pointer ${
                    activeTab === tab.key
                      ? 'border-emerald-600 text-emerald-600'
                      : 'border-transparent text-stone-500 hover:text-stone-700'
                  }`}
                >
                  <i className={`${tab.icon} text-xs md:text-sm shrink-0`}></i>
                  <span className="truncate hidden xs:inline sm:inline">{tab.label}</span>
                </button>
              ))}
            </div>
            {/* External Links - hidden on mobile, shown on md+ */}
            <div className="hidden md:flex items-center gap-2 shrink-0 pb-1 mr-2">
              <Link
                to="/create"
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-circle-line text-xs"></i>
                حملة جديدة
              </Link>
              <Link
                to="/explore"
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-white border border-stone-200 text-stone-600 hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-compass-3-line text-xs"></i>
                استكشاف
              </Link>
            </div>
          </div>
          {/* Mobile action buttons */}
          <div className="flex md:hidden items-center gap-2 px-4 py-2 border-b border-stone-100">
            <Link
              to="/create"
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors cursor-pointer"
            >
              <i className="ri-add-circle-line text-xs"></i>
              حملة جديدة
            </Link>
            <Link
              to="/explore"
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-white border border-stone-200 text-stone-600 hover:bg-stone-50 transition-colors cursor-pointer"
            >
              <i className="ri-compass-3-line text-xs"></i>
              استكشاف
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-8 py-6">

        {/* ── Overview Tab ── */}
        {activeTab === 'overview' && (
          <>
            <div className="mb-6">
              <DashboardStats profile={dashboardProfile} />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              <div className="lg:col-span-2">
                <DashboardChart />
              </div>
              <div>
                <DashboardDonors />
              </div>
            </div>
            <DashboardCampaigns campaigns={myCampaigns} />
          </>
        )}

        {/* ── Campaigns Tab ── */}
        {activeTab === 'campaigns' && (
          <DashboardCampaigns campaigns={myCampaigns} />
        )}

        {/* ── Donors Tab ── */}
        {activeTab === 'donors' && (
          <div className="max-w-2xl">
            <DashboardDonors />
          </div>
        )}

        {/* ── History Tab ── */}
        {activeTab === 'history' && (
          <div className="max-w-3xl">
            <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100">
                <div>
                  <h3 className="font-black text-stone-800 text-base">تاريخ السحوبات</h3>
                  <p className="text-xs text-stone-400 mt-0.5">{withdrawalHistory.length} عملية</p>
                </div>
                <button
                  onClick={exportPDF}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-stone-800 hover:bg-stone-700 text-white transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-file-pdf-2-line text-sm"></i>
                  تصدير PDF
                </button>
              </div>

              {/* Summary */}
              <div className="grid grid-cols-3 gap-px bg-stone-100">
                {[
                  {
                    label: 'إجمالي المسحوب',
                    amount: withdrawalHistory.filter(w => w.status === 'completed').reduce((s, w) => s + w.net, 0),
                    icon: 'ri-money-dollar-circle-line',
                    color: 'text-emerald-600',
                    isMoney: true,
                  },
                  {
                    label: 'عدد العمليات',
                    amount: withdrawalHistory.filter(w => w.status === 'completed').length,
                    icon: 'ri-checkbox-circle-line',
                    color: 'text-stone-700',
                    isMoney: false,
                  },
                  {
                    label: 'قيد الانتظار',
                    amount: withdrawalHistory.filter(w => w.status === 'pending').reduce((s, w) => s + w.net, 0),
                    icon: 'ri-time-line',
                    color: 'text-amber-600',
                    isMoney: true,
                  },
                ].map((stat, i) => (
                  <div key={i} className="bg-stone-50 px-5 py-4 text-center">
                    <div className="w-8 h-8 flex items-center justify-center mx-auto mb-1">
                      <i className={`${stat.icon} text-lg ${stat.color}`}></i>
                    </div>
                    <p className={`text-base font-black ${stat.color}`}>
                      {stat.isMoney ? <SAR amount={stat.amount} iconSize={14} /> : stat.amount}
                    </p>
                    <p className="text-xs text-stone-400 mt-0.5">{stat.label}</p>
                  </div>
                ))}
              </div>

              {/* List */}
              <div className="divide-y divide-stone-50">
                {[...withdrawalHistory].reverse().map((w) => {
                  const statusConfig = {
                    completed:  { label: 'مكتملة',       color: 'bg-emerald-100 text-emerald-700', icon: 'ri-checkbox-circle-fill' },
                    pending:    { label: 'قيد الانتظار', color: 'bg-amber-100 text-amber-700',     icon: 'ri-time-fill' },
                    processing: { label: 'جاري التحويل', color: 'bg-sky-100 text-sky-700',         icon: 'ri-loader-4-line' },
                  }[w.status];

                  const dateFormatted = new Date(w.date).toLocaleDateString('en-GB', {
                    year: 'numeric', month: 'short', day: 'numeric',
                  });

                  return (
                    <div key={w.id} className="flex items-center gap-4 px-6 py-4 hover:bg-stone-50 transition-colors">
                      {/* Icon */}
                      <div className={`w-10 h-10 flex items-center justify-center rounded-xl shrink-0 ${
                        w.status === 'completed' ? 'bg-emerald-50' : w.status === 'pending' ? 'bg-amber-50' : 'bg-sky-50'
                      }`}>
                        <i className={`${statusConfig.icon} text-lg ${
                          w.status === 'completed' ? 'text-emerald-600' : w.status === 'pending' ? 'text-amber-600' : 'text-sky-600'
                        }`}></i>
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-sm font-bold text-stone-800">
                            <SAR amount={w.net} iconSize={12} />
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusConfig.color}`}>
                            {statusConfig.label}
                          </span>
                        </div>
                        <p className="text-xs text-stone-400">
                          {w.bankName} •••• {w.accountLast4}
                        </p>
                        <p className="text-xs text-stone-300 mt-0.5">{w.reference}</p>
                      </div>

                      {/* Date + Fee */}
                      <div className="text-left shrink-0">
                        <p className="text-xs font-medium text-stone-600">{dateFormatted}</p>
                        {w.fee > 0 && (
                          <p className="text-xs text-stone-400 mt-0.5">
                            رسوم: <SAR amount={w.fee} iconSize={11} />
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── Withdrawal Tab ── */}
        {activeTab === 'withdrawal' && (
          <div className="max-w-2xl">
            <div className="bg-white rounded-2xl border border-stone-100 p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-emerald-50 rounded-xl">
                  <i className="ri-bank-line text-emerald-600 text-xl"></i>
                </div>
                <div>
                  <p className="text-xs text-stone-400 mb-0.5">الصافي المتاح للسحب</p>
                  <p className="text-3xl font-black text-stone-800">
                    <SAR amount={dashboardProfile.pendingWithdrawal} iconSize={22} />
                  </p>
                  <p className="text-xs text-stone-400 mt-0.5">
                    بعد خصم رسوم المنصة {Math.round(dashboardProfile.platformFeeRate * 100)}%
                    &nbsp;(<SAR amount={dashboardProfile.platformFee} iconSize={11} />)
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                {[
                  { icon: 'ri-time-line', label: 'مدة التحويل', value: '3-5 أيام عمل', color: 'text-stone-500' },
                  { icon: 'ri-shield-check-line', label: 'الأمان', value: 'تحويل مشفر', color: 'text-emerald-600' },
                  { icon: 'ri-bank-card-line', label: 'الوجهة', value: 'حسابك البنكي', color: 'text-stone-500' },
                ].map((item, i) => (
                  <div key={i} className="bg-stone-50 rounded-xl p-4 text-center">
                    <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                      <i className={`${item.icon} text-lg ${item.color}`}></i>
                    </div>
                    <p className="text-xs text-stone-400 mb-0.5">{item.label}</p>
                    <p className="text-xs font-bold text-stone-700">{item.value}</p>
                  </div>
                ))}
              </div>

              <button
                onClick={() => setShowWithdrawal(true)}
                className="w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-arrow-down-circle-line"></i>
                طلب سحب الأموال
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <WithdrawalModal
        isOpen={showWithdrawal}
        onClose={() => setShowWithdrawal(false)}
        totalRaised={dashboardProfile.totalRaised}
        platformFeeRate={dashboardProfile.platformFeeRate}
        platformFee={dashboardProfile.platformFee}
        availableBalance={dashboardProfile.pendingWithdrawal}
        ownerName={dashboardProfile.displayName}
      />
      <PostUpdateModal
        isOpen={showPostUpdate}
        onClose={() => setShowPostUpdate(false)}
        campaigns={myCampaigns}
      />
    </div>
  );
};

export default DashboardPage;
